import {CalendarOptions, EventSetData} from '@taopix/taopix-core/dist/types/types';
import {Heading, LoadingOverlay} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';
import ToolPaletteSection from '../../ToolPalette/ToolPaletteSection';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {useTranslation} from 'react-i18next';

export type EventSetsListPaletteProps = {
  sectionKey: string;
  selectedEventSet: any;
  calendarSettings: CalendarOptions;
};

const EventSetsListPalette = ({sectionKey, selectedEventSet}: EventSetsListPaletteProps) => {
  const calendarManager = window.Taopix.Core.get('calendarManager');
  const ui = useUiState();
  const {t, i18n} = useTranslation();

  const [awaitingResponse, setAwaitingResponse] = useState<boolean>(true);
  const [selectedEventSetDates, setSelectedEventSetDates] = useState<EventSetData>();

  const langCode = useMemo(() => {
    return i18n.language.startsWith('zh') ? i18n.language.split('_').join('-') : i18n.language;
  }, [i18n.language]);

  // show loading spinner if new event set is being loaded in
  useEffect(() => {
    if (selectedEventSet) {
      setAwaitingResponse(true);
      calendarManager.getEventSetDates(selectedEventSet?.setUniqueRef).then((response: any) => {
        setSelectedEventSetDates(response);
        if (response) {
          setAwaitingResponse(false);
        }
      });
    }
  }, [selectedEventSet?.setUniqueRef]);

  const sectionButtonClasses = classNames('');

  const wrapperClasses = classNames(
    'flex-1',
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'sm:pb-md',
    'overflow-y-auto'
  );

  function getDaySuffix(day) {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  const formattedData = {};
  for (const date in selectedEventSetDates) {
    const year = date.split('-')[0];
    if (!(year in formattedData)) {
      formattedData[year] = [];
    }
    formattedData[year].push({[`${date}`]: selectedEventSetDates[date]});
  }

  return (
    <ToolPaletteSection
      id={'calendarEventsListPalette'}
      title={selectedEventSet?.name as unknown as string} // string
      sectionKey={`${sectionKey}-${selectedEventSet?.setUniqueRef}`}
      buttonClasses={sectionButtonClasses}
      subSectionPanelType={ui.state.isSmallScreen ? 'pop-up' : 'slide-in'}
      smallScreenPanelHeight="full"
      subSection={true}
      showButton={false}
      buttonLayout={'horizontal'}
      parentselected={true}
    >
      <div className={wrapperClasses}>
        {awaitingResponse && (
          <LoadingOverlay open={true} usePortal={false} overlayColour={'themeBackgroundColour'} />
        )}
        {Object.entries(formattedData).map(([date, events]: any) => {
          return (
            <div key={date} className={'pb-md'}>
              <Heading className={'border-b border-themeTextColour20 mb-sm pb-xs'}>{date}</Heading>
              {events.map((event: any) => {
                return Object.entries(event).map(([date, eventName]: any) => {
                  const newDate = new Date(date);
                  const day = newDate?.getDate();
                  const monthShort = new Intl.DateTimeFormat(langCode, {month: 'short'})?.format(
                    newDate
                  );
                  const dayWithSuffix = day + getDaySuffix(day);
                  return (
                    <div
                      key={`${date}-${eventName}`}
                      className={
                        'flex justify-between py-xs leading-snug text-sm border-b border-b-themeTextColour20'
                      }
                    >
                      <p
                        className={'whitespace-nowrap mr-sm'}
                      >{`${dayWithSuffix} ${monthShort}`}</p>
                      <p className={'text-right'}>{eventName}</p>
                    </div>
                  );
                });
              })}
            </div>
          );
        })}
      </div>
    </ToolPaletteSection>
  );
};

export default EventSetsListPalette;
